import React, { useState, useRef, useEffect, createContext, useContext } from "react";
// import { YoutubeEmbedByLink } from "../embed/youtube";
import * as yt from "../embed/iframeApi";
import Slider from "react-slick";
import utils from "../../utils";
import { FaTimes } from "react-icons/fa";
import { SiteLink } from "../../components";

// create context for video playback
const VideoPlaybackContext = createContext();

// video playback manager
function VideoPlaybackProvider({ children }) {
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  
  const playVideo = (videoId) => {
    setCurrentPlayingId(videoId);
  };
  
  const stopVideo = (videoId) => {
    if (currentPlayingId === videoId) {
      setCurrentPlayingId(null);
    }
  };
  
  const isPlaying = (videoId) => {
    return currentPlayingId === videoId;
  };
  
  return (
    <VideoPlaybackContext.Provider value={{ playVideo, stopVideo, isPlaying }}>
      {children}
    </VideoPlaybackContext.Provider>
  );
}

// use video playback context hook - provide fallback
function useVideoPlayback() {
  const context = useContext(VideoPlaybackContext);
  return context; // return context, even if it's null, it's ok, in VideoPanel it's handled
}

// Custom hook to detect mobile screen size
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 576);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return isMobile;
}

// Homepage Section Components - need to be wrapped in Provider
export function HomepageSection({ items }) {
  return (
    <VideoPlaybackProvider>
      {items.map((item, index) => <HomepageItem key={index} item={item} />)}
    </VideoPlaybackProvider>
  );
}

export function HomepageItem({ item }) {
    if (item.itemTypeCode === "bannerpanel") {
      return <BannerPanel item={item} />;
    }
    if (item.itemTypeCode === "videopanel") {
      return <VideoPanel item={item} />;
    }
    if (item.itemTypeCode === "carouselpanel") {
      return <CarouselPanel item={item} />;
    }
    return null;
  }

// export for other components
export { VideoPlaybackProvider, useVideoPlayback };
  
  function CarouselPanel({ item }) {
    const data = JSON.parse(item.itemValue);
    const allImages = data.images || [];
    const isMobile = useIsMobile();
    
    // Filter images based on screen size and desktopOrMobile field
    const images = allImages.filter(image => {
      const deviceType = image.desktopOrMobile || "desktop"; // default to desktop if not specified
      return isMobile ? deviceType === "mobile" : deviceType === "desktop";
    });
    
    // Determine aspect ratio based on screen size
    const aspectRatio = isMobile ? '4/3' : '5/2';

  // Add custom styles for banner panel dots and arrows
    useEffect(() => {
      // Only add styles if there are images to show
      if (images.length === 0) {
        return;
      }
      const styleId = 'banner-panel-dots-style';
      
      // Remove existing style if present
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      // Add new style
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .homepage-banner-panel .slick-dots {
          position: absolute !important;
          bottom: 40px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          margin: 0 !important;
          padding: 0 !important;
          list-style: none !important;
          display: flex !important;
          gap: 12px !important;
          z-index: 10 !important;
          justify-content: center;
        }
        
        .homepage-banner-panel .slick-dots li {
          width: auto !important;
          height: auto !important;
          margin: 0 !important; 
        }
        
        .homepage-banner-panel .slick-dots li button {
          width: 16px !important;
          height: 16px !important;
          border-radius: 50% !important;
          background-color: rgba(255, 255, 255, 0.5) !important;
          border: 2px solid rgba(0, 0, 0, 0.8) !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          font-size: 0 !important;
          padding: 0 !important;
        }
        
        .homepage-banner-panel .slick-dots li button:hover {
          background-color: rgba(0, 0, 0, 0.8) !important;
          transform: scale(1.2) !important;
        }
        
        .homepage-banner-panel .slick-dots li.slick-active button {
          background-color: #FFA500 !important;
          border-color: rgba(0, 0, 0, 1) !important;
        }
        
        .homepage-banner-panel .slick-dots li button:before {
          display: none !important;
        }

        /* Clean SVG Arrow Styles - No background, enlarged icons */
        .homepage-banner-panel .slick-prev,
        .homepage-banner-panel .slick-next {
          z-index: 10 !important;
          width: 50px !important;
          height: 50px !important;
          background: none !important;
          border: none !important;
          box-shadow: none !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          transition: all 0.3s ease !important;
          cursor: pointer !important;
        }

        .homepage-banner-panel .slick-prev {
          left: -50px !important;
        }

        .homepage-banner-panel .slick-next {
          right: -50px !important;
        }

        .homepage-banner-panel .slick-prev:hover,
        .homepage-banner-panel .slick-next:hover {
          transform: translateY(-50%) scale(1.1) !important;
        }

        /* Large SVG Arrow Icons - Fill the entire 50px container */
        .homepage-banner-panel .slick-prev:before {
          content: '' !important;
          display: block !important;
          width: 50px !important;
          height: 50px !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23000'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M15 19l-7-7 7-7'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
          opacity: 0.7 !important;
          transition: opacity 0.3s ease !important;
        }

        .homepage-banner-panel .slick-next:before {
          content: '' !important;
          display: block !important;
          width: 50px !important;
          height: 50px !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23000'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M9 5l7 7-7 7'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
          opacity: 0.7 !important;
          transition: opacity 0.3s ease !important;
        }

        .homepage-banner-panel .slick-prev:hover:before,
        .homepage-banner-panel .slick-next:hover:before {
          opacity: 1 !important;
        }

        /* Mobile responsive arrows */
        @media (max-width: 576px) {
          .homepage-banner-panel .slick-prev,
          .homepage-banner-panel .slick-next {
            width: 30px !important;
            height: 30px !important;
          }

          .homepage-banner-panel .slick-prev {
            left: -40px !important; /* adjust outer distance when mobile */
          }

          .homepage-banner-panel .slick-next {
            right: -40px !important; /* adjust outer distance when mobile */
          }

          .homepage-banner-panel .slick-prev:before,
          .homepage-banner-panel .slick-next:before {
            width: 30px !important;
            height: 30px !important;
          }
        }
      `;
      document.head.appendChild(style);

      // Cleanup function
      return () => {
        const styleToRemove = document.getElementById(styleId);
        if (styleToRemove) {
          styleToRemove.remove();
        }
      };
    }, []);

    const settings = {
      dots: images.length > 1,  // only show dots when there are multiple images
      infinite: images.length > 1,
    speed: 1000,
      autoplay: data.autoRotate || false,
      autoplaySpeed: data.rotateInterval || 3000,
      slidesToShow: 1,
      slidesToScroll: 1,
    fade: false, // change to slide effect
      arrows: images.length > 1,
    };

    console.log('speed', settings.autoplaySpeed);

    // If no images, return null
    if (images.length === 0) {
      return null;
    }
  
    return (
      <div className="homepage-banner-panel">
        <div 
          className="homepage-banner-panel__container" 
          style={{ 
          // aspectRatio: aspectRatio,
          overflow: 'visible', // change to visible to show outer arrows
          position: 'relative',
          padding: isMobile ? '0 40px' : '0 50px', // set arrow space according to device type
        }}
      >
        <div
          style={{
            // overflow: 'hidden', // inner container controls image overflow
            aspectRatio: aspectRatio, 
            position: 'relative'
          }}
        >
          <Slider {...settings}>
            {images.map((image, index) => (
              <div key={index} className="homepage-banner-panel__slide">
                {image.linkUrl ? (
                  <SiteLink to={image.linkUrl}>
                    <div 
                      className="homepage-banner-panel__image"
                      style={{
                        aspectRatio: aspectRatio,
                        backgroundImage: `url(${utils.site.resourcePath(image.imageUrl)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    />
                  </SiteLink>
                ) : (
                  <div 
                    className="homepage-banner-panel__image"
                    style={{
                      aspectRatio: aspectRatio,
                      backgroundImage: `url(${utils.site.resourcePath(image.imageUrl)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                )}
              </div>
            ))}
          </Slider>
        </div>
        </div>
      </div>
    );
  }
  
function VideoPanel({ item }) {
  const data = JSON.parse(item.itemValue);
  const isMobile = useIsMobile();
  const allImages = data.images || [];

  // Advanced animation states - MUST BE AT THE TOP to follow hooks rules
  const [isVideoEntering, setIsVideoEntering] = useState(false);
  const [isPosterExiting, setIsPosterExiting] = useState(false);

  const videoId = data.videoUrl ? yt.getYoutubeCode(data.videoUrl) : null;

  // try to use global video playback management
  const globalVideoContext = useVideoPlayback();
  
  // local playback state (for independent mode)
  const [localPlaying, setLocalPlaying] = useState(false);
  
  // generate stable unique video ID - use useMemo to ensure no duplication
  const uniqueVideoId = React.useMemo(() => {
    // use videoId and item content to generate stable ID
    const itemHash = JSON.stringify(item).slice(0, 10); // take the first 10 characters as a short identifier
    return `video-${videoId}-${itemHash}`;
  }, [videoId, item]);
  
  // if there is global context, use global management; otherwise use local state
  const isUsingGlobalContext = !!globalVideoContext;
  
  let currentlyPlaying, handlePlayVideo, handleCloseVideo;
  
  if (isUsingGlobalContext) {
    // global mode: support exclusive playback
    const { playVideo, stopVideo, isPlaying } = globalVideoContext;
    
    currentlyPlaying = isPlaying(uniqueVideoId);
    handlePlayVideo = () => playVideo(uniqueVideoId);
    handleCloseVideo = () => stopVideo(uniqueVideoId);
  } else {
    // local mode: independent playback
    currentlyPlaying = localPlaying;
    handlePlayVideo = () => setLocalPlaying(true);
    handleCloseVideo = () => setLocalPlaying(false);
  }
  
  // Find the appropriate poster image
  const posterImage = allImages.find(image => {
    const deviceType = image.desktopOrMobile || "desktop";
    return isMobile ? deviceType === "mobile" : deviceType === "desktop";
  });
  
  // Determine aspect ratios
  const posterRatio = isMobile ? '4/3' : '5/2';
  const videoRatio = '16/9';

  // Container styles
  const containerStyle = {
    aspectRatio: currentlyPlaying ? videoRatio : posterRatio,
    overflow: currentlyPlaying ? 'visible' : 'hidden',
    position: 'relative',
    transition: 'aspect-ratio 0.3s ease-in-out',
    backgroundColor: '#000',
    backgroundImage: posterImage && !currentlyPlaying ? `url(${utils.site.resourcePath(posterImage.imageUrl)})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // Play button styles
  const playButtonStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 5,
  };

  const PlayButton = ({ onClick, onMouseEnter, onMouseLeave }) => (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={playButtonStyle}
      title="Play video"
    >
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        style={{ 
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))',
          transition: 'all 0.3s ease',
        }}
      >
        {/* Outer circle - white semi-transparent */}
        <circle
          cx="40"
          cy="40"
          r="38"
          fill="rgba(255, 255, 255, 0.9)"
          stroke="rgba(255, 255, 255, 0.95)"
          strokeWidth="2"
        />
        {/* Play triangle - black */}
        <polygon
          points="32,26 32,54 54,40"
          fill="#000"
        />
     </svg>
             </div>
 );

  // Enhanced Close button with advanced animations (zero dependencies)
  const CloseButton = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    // Entrance animation on mount
    useEffect(() => {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }, []);

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
          setIsPressed(false);
        }}
        onMouseDown={() => setIsPressed(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsPressed(false);
        }}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          width: '40px',
          height: '40px',
          cursor: 'pointer',
          zIndex: 2,
          pointerEvents: 'auto',
          borderRadius: '50%',
          background: isHovered ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `scale(${
            !isVisible ? 0.8 : isPressed ? 0.95 : isHovered ? 1.1 : 1
          })`,
          opacity: isVisible ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: isVisible 
            ? isHovered 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 2px 10px rgba(0, 0, 0, 0.2)'
            : 'none',
        }}
        title="Close video"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: `rotate(${isHovered ? 90 : 0}deg)`,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none'
          }}
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
    );
  };

  // Handle video play with animation
  const handleVideoPlayWithAnimation = () => {
    setIsPosterExiting(true);
    setTimeout(() => {
      handlePlayVideo();
      setIsVideoEntering(true);
      setTimeout(() => setIsVideoEntering(false), 400);
    }, 300);
  };

  // Handle video close with animation
  const handleVideoCloseWithAnimation = () => {
    setIsVideoEntering(false);
    handleCloseVideo();
    setTimeout(() => setIsPosterExiting(false), 100);
  };

  // If no video URL, return null - MOVED AFTER ALL HOOKS
  if (!data.videoUrl || !videoId) {
    return null;
  }

  return (
    <div className="homepage-video-panel">
      <div 
        className="homepage-video-panel__container" 
        style={{
          ...containerStyle,
          transition: 'aspect-ratio 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {!currentlyPlaying ? (
          // Poster view with play button
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: isPosterExiting ? 0 : 1,
              transform: isPosterExiting ? 'scale(0.95)' : 'scale(1)',
              transition: 'all 0.3s ease-out',
            }}
          >
            <PlayButton
              onClick={handleVideoPlayWithAnimation}
              onMouseEnter={(e) => {
                const svg = e.currentTarget.querySelector('svg');
                if (svg) {
                  svg.style.transform = 'scale(1.1)';
                  svg.style.filter = 'drop-shadow(0 6px 16px rgba(0, 0, 0, 0.4))';
                }
              }}
              onMouseLeave={(e) => {
                const svg = e.currentTarget.querySelector('svg');
                if (svg) {
                  svg.style.transform = 'scale(1)';
                  svg.style.filter = 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))';
                }
              }}
            />
          </div>
        ) : (
          // Video view with enhanced animations
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              opacity: isVideoEntering ? 0 : 1,
              transform: isVideoEntering ? 'scale(0.95)' : 'scale(1)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <iframe
              width="100%"
              height="100%"
              title={data.title || "Youtube video"}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0&modestbranding=0&showinfo=1&allowfullscreen=1&enablejsapi=0&fs=1&cc_load_policy=0`}
              frameBorder="0"
              allowFullScreen
              scrolling="no"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent'
              }}
            />
            {/* Enhanced close button */}
            <CloseButton onClick={handleVideoCloseWithAnimation} />
          </div>
        )}
      </div>
      
      {/* debug info with smooth animation */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div 
          style={{ 
            fontSize: '0.8rem', 
            color: '#666', 
            marginTop: '0.5rem',
            opacity: 1,
            transform: 'translateY(0)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          Mode: {isUsingGlobalContext ? 'Global (Exclusive)' : 'Local (Independent)'}
          {isUsingGlobalContext && (
            <div>ID: {uniqueVideoId}</div>
          )}
        </div>
      )} */}
    </div>
  );
  }

  function BannerPanel({ item }) {
    const data = JSON.parse(item.itemValue);
    const allImages = data.images || [];
    const isMobile = useIsMobile();
    
    // Filter images based on screen size and desktopOrMobile field
    const images = allImages.filter(image => {
      const deviceType = image.desktopOrMobile || "desktop"; // default to desktop if not specified
      return isMobile ? deviceType === "mobile" : deviceType === "desktop";
    });
    
    // Determine aspect ratio based on screen size
  const aspectRatio = isMobile ? '4/3' : '5/2';

    if (images.length === 0) {
      return null;
    }

    // Get the first (and only) image
    const image = images[0];

    return (
      <div className="homepage-banner-panel homepage-single-banner">
        <div 
          className="homepage-banner-panel__container" 
          style={{ 
            aspectRatio: aspectRatio, 
            overflow: 'hidden',
            position: 'relative',
          // borderRadius: '8px'
          }}
        >
          {image.linkUrl ? (
            <SiteLink to={image.linkUrl}>
              <div 
                className="homepage-banner-panel__image"
                style={{
                  aspectRatio: aspectRatio,
                  backgroundImage: `url(${utils.site.resourcePath(image.imageUrl)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  width: '100%',
                  height: '100%'
                }}
              />
            </SiteLink>
          ) : (
            <div 
              className="homepage-banner-panel__image"
              style={{
                aspectRatio: aspectRatio,
                backgroundImage: `url(${utils.site.resourcePath(image.imageUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: '100%',
                height: '100%'
              }}
            />
          )}
        </div>
      </div>
    );
  }
  